"use client";

import { Card, CardContent } from "@/components/ui/card";
import { useState } from "react";
import { BlogItem, getBlogs } from "@/utils/getBlogs";
import GcsImage, { GcsPath } from "@/components/common/GcsImage";
import Link from "next/link";
import { ChevronDown } from "lucide-react";
import { Progress } from "../ui/progress";

function normalizeSrc(src: string): string {
  return src.startsWith("/") ? src.slice(1) : src;
}

export function BlogsGrid() {
  const ITEMS_PER_PAGE = 9;
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);

  const blogs: BlogItem[] = getBlogs();

  const visibleBlogs = blogs.slice(0, visibleCount);
  const hasMore = visibleCount < blogs.length;

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + ITEMS_PER_PAGE);
  };

  return (
    <section className="py-16 bg-muted/30">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-foreground">
          Latest Blog Posts
        </h2>

        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {visibleBlogs.map(({ slug, path, meta }) => (
              <Link key={slug} href={path} className="block">
                <Card className="border-0 shadow-lg min-h-96 flex flex-col hover:shadow-xl transition-shadow">
                  <CardContent className="p-4 flex flex-col flex-1">
                    <div className="relative w-full aspect-video mb-3 bg-primary/10 rounded-lg overflow-hidden">
                      <GcsImage
                        path={normalizeSrc(meta.featuredImage.src) as GcsPath}
                        alt={meta.featuredImage.alt}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    </div>

                    <h3 className="text-lg font-bold mb-2 text-foreground  md:line-clamp-3">
                      {meta.title}
                    </h3>

                    <p className="text-sm text-muted-foreground md:line-clamp-5">
                      {meta.description}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          {/* INFO TEXT */}
          <div className="text-center mt-10 text-sm text-muted-foreground">
            Showing {Math.min(visibleCount, blogs.length)} of {blogs.length}{" "}
            articles
          </div>

          <Progress
            value={(visibleCount / blogs.length) * 100}
            className="h-2 w-[40%] bg-muted mx-auto"
            barClassName="bg-primary"
          />

          {/* LOAD MORE BUTTON */}
          {hasMore && (
            <div className="flex justify-center mt-6">
              <button
                onClick={handleLoadMore}
                className="px-6 py-3 cursor-pointer rounded-lg bg-primary text-primary-foreground font-medium hover:opacity-90 transition inline-flex items-center gap-2"
              >
                Load 9 more <ChevronDown />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
